import type { RevenueByPlanDto } from '@planuze/admin-contracts';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useMetricsWidgetsContent } from '../../content/sections/metrics-widgets.js';
import { type Currency, fmtCurrency, fmtNumber } from '../../lib/format.js';
import { ChartCard } from './chart-card.js';
import { colorAt } from './chart-palette.js';

interface Props {
  readonly data: RevenueByPlanDto | undefined;
  readonly loading: boolean;
  /** A barra é contagem de clientes; a moeda anota o MRR do plano no tooltip. */
  readonly currency: Currency;
  readonly hasFilters?: boolean | undefined;
  readonly onClearFilters?: (() => void) | undefined;
}

/* Clientes por plano (bar). Vem de `revenueByPlanDto.items[].customerCount` — não há
   endpoint dedicado de signups-por-plano (ADR-0359). O tooltip mostra o MRR do plano na
   moeda selecionada usando os campos que já existem no DTO (mrrUsd/mrrBrl). */
export function CustomersByPlanChart({
  data,
  loading,
  currency,
  hasFilters,
  onClearFilters,
}: Props) {
  const copy = useMetricsWidgetsContent();
  const rows = (data?.items ?? []).map((item) => ({
    plan: item.plan,
    customerCount: item.customerCount,
    mrr: currency === 'USD' ? item.mrrUsd : item.mrrBrl,
  }));
  return (
    <ChartCard
      title={copy.customersByPlanChart.title}
      loading={loading}
      empty={rows.length === 0}
      hasFilters={hasFilters}
      onClearFilters={onClearFilters}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="plan" fontSize={12} />
          <YAxis tickFormatter={fmtNumber} fontSize={12} allowDecimals={false} />
          <Tooltip
            formatter={(value: unknown) => [
              fmtNumber(Number(value)),
              copy.customersByPlanChart.tooltipCustomers,
            ]}
            labelFormatter={(label: unknown) => {
              const row = rows.find((r) => r.plan === String(label));
              return row === undefined
                ? String(label)
                : `${row.plan} · ${fmtCurrency(row.mrr, currency)} MRR`;
            }}
          />
          <Bar dataKey="customerCount" fill={colorAt(1)} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
